import {
  Button,
  Card,
  CardContent,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Typography,
} from "@mui/material";
import ClientSummaryCard from "components/ClientSummaryCard";
import GstChargeField from "components/GstChargeField";
import InputField from "components/Input/InputField";
import ClientDropdown from "components/Dropdowns/ClientDropdown";
import useKothar from "context/useKothar";
import { computeFinalAmount, computeTotal } from "utils/gstCalc";
import { useEffect } from "react";

const InfoSection = ({ title, children }) => (
  <Card className="mb-3 shadow-sm border border-app-border">
    <CardContent className="p-0">
      <div className="px-4 py-2 border-b border-app-border bg-orange-50">
        <Typography variant="subtitle2" className="font-semibold text-orange-700 text-sm">
          {title}
        </Typography>
      </div>
      <div className="p-3">{children}</div>
    </CardContent>
  </Card>
);

const OverviewTab = ({
  data,
  setData,
  rplList,
  id,
  setOpenAlertModal,
  handleInputChange,
  isLoading,
  navigate,
}) => {
  const [{ clientList }, { refetchClient }] = useKothar();
  const clientId = data?.clientId ?? data?.id;
  const clientCaseOfficer = clientList?.find((client) => client.id === clientId)?.caseOfficer;

  useEffect(() => {
    if (!clientList?.length) refetchClient();
  }, [clientList?.length, refetchClient]);

  return (
    <div className="space-y-4">
      {!data?.id && (
        <div className="mb-4">
          <ClientDropdown
            {...{
              data,
              setData,
              onChange: (value) => {
                if (!id && !data?.clientId) {
                  const rpl = rplList?.filter((item) => item?.clientId === value?.id);

                  if (rpl?.length > 0) {
                    setOpenAlertModal({
                      state: true,
                      message: "RPL Data already exists",
                      data: rpl,
                    });
                  }
                }
              },
            }}
          />
        </div>
      )}

      <ClientSummaryCard
        data={data}
        clientList={clientList}
        onViewClient={() => clientId && navigate(`/admin/client/view/${clientId}`)}
      />

      {!clientId && data?.id && (
        <Card className="shadow-sm border border-app-border">
          <CardContent className="p-3 text-center">
            <Typography variant="body2" className="text-app-text-secondary text-sm">
              No client information available
            </Typography>
          </CardContent>
        </Card>
      )}

      <InfoSection title="Course & Other Details">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <InputField
            fullWidth
            label="Certificate Name"
            placeholder="Enter Certificate Name"
            name="certificate"
            type="text"
            value={data?.certificate || ""}
            onChange={handleInputChange}
          />
          <InputField
            fullWidth
            label="Currently Enrolled Course"
            placeholder="Currently Enrolled Course"
            name="currentlyEnrolledCourse"
            type="text"
            value={data?.currentlyEnrolledCourse || ""}
            onChange={handleInputChange}
          />
          <InputField
            fullWidth
            label="Currently Enrolled University"
            placeholder="Currently Enrolled University"
            name="currentlyEnrolledUniversity"
            type="text"
            value={data?.currentlyEnrolledUniversity || ""}
            onChange={handleInputChange}
          />
          <InputField
            fullWidth
            label="Course Start Date according to the current COE"
            placeholder="Course Start Date according to the current COE"
            name="courseEndDate"
            type="date"
            value={data?.courseEndDate || ""}
            onChange={handleInputChange}
          />
          <div className="relative w-full">
            <FormControl fullWidth>
              <FormLabel className="text-app-text-secondary uppercase text-xs font-bold mb-2">
                Placement Required
              </FormLabel>
              <RadioGroup
                row
                name="placementRequired"
                value={data?.placementRequired || ""}
                onChange={handleInputChange}
              >
                <FormControlLabel value="yes" control={<Radio />} label="Yes" />
                <FormControlLabel value="no" control={<Radio />} label="No" />
              </RadioGroup>
            </FormControl>
          </div>
          <div className="relative w-full">
            <FormControl fullWidth>
              <FormLabel className="text-app-text-secondary uppercase text-xs font-bold mb-2">
                Follow Up Required (Course Change/Extension)
              </FormLabel>
              <RadioGroup
                row
                name="insuranceStudentCard"
                value={data?.insuranceStudentCard || ""}
                onChange={handleInputChange}
              >
                <FormControlLabel value="yes" control={<Radio />} label="Yes" />
                <FormControlLabel value="no" control={<Radio />} label="No" />
              </RadioGroup>
            </FormControl>
          </div>
          <InputField
            fullWidth
            label="Case Officer"
            placeholder="Case Officer"
            name="caseOfficer"
            type="text"
            value={data?.caseOfficer || clientCaseOfficer || ""}
            onChange={handleInputChange}
            disabled
          />
          <InputField
            fullWidth
            label="Reference"
            placeholder="Reference"
            name="reference"
            type="text"
            value={data?.reference || ""}
            onChange={handleInputChange}
          />
        </div>

        {data?.status === "Placement Processing" && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4 pt-4 border-t border-app-border">
            <InputField
              fullWidth
              label="Site Name"
              placeholder="Site Name"
              name="siteName"
              type="text"
              value={data?.siteName || ""}
              onChange={handleInputChange}
            />
            <InputField
              fullWidth
              label="Start Date of Placement"
              placeholder="Start Date of Placement"
              name="placementStartDate"
              type="date"
              value={data?.placementStartDate || ""}
              onChange={handleInputChange}
            />
          </div>
        )}
      </InfoSection>

      <InfoSection title="Charges">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <GstChargeField
            label="Cost"
            amountName="cost"
            amountValue={data?.cost}
            onAmountChange={handleInputChange}
            gst={data?.costGst}
            onGstChange={(next) => setData((prev) => ({ ...prev, costGst: next }))}
          />
          <GstChargeField
            label="Service charge"
            amountName="serviceCharge"
            amountValue={data?.serviceCharge}
            onAmountChange={handleInputChange}
            gst={data?.serviceChargeGst}
            onGstChange={(next) => setData((prev) => ({ ...prev, serviceChargeGst: next }))}
          />
          <GstChargeField
            label="Extra charges"
            amountName="extraCharges"
            amountValue={data?.extraCharges}
            onAmountChange={handleInputChange}
            gst={data?.extraChargesGst}
            onGstChange={(next) => setData((prev) => ({ ...prev, extraChargesGst: next }))}
          />
          {/* <GstChargeField
            label="Kothar cost (optional)"
            amountName="kotharCost"
            amountValue={data?.kotharCost}
            onAmountChange={handleInputChange}
            gst={data?.kotharCostGst}
            onGstChange={(next) => setData((prev) => ({ ...prev, kotharCostGst: next }))}
          /> */}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4 pt-4 border-t border-app-border">
          <InputField
            fullWidth
            label="Total (auto-calculated)"
            placeholder="0.00"
            name="totalPreview"
            type="number"
            disabled
            value={computeTotal(
              {
                primaryCharge: data?.cost,
                serviceCharge: data?.serviceCharge,
                extraCharges: data?.extraCharges,
              },
              {
                primaryChargeGst: data?.costGst,
                serviceChargeGst: data?.serviceChargeGst,
                extraChargesGst: data?.extraChargesGst,
              }
            ).toFixed(2)}
          />
          <InputField
            fullWidth
            label="Discount (optional)"
            placeholder="0.00"
            name="discount"
            type="number"
            value={data?.discount ?? ""}
            onChange={handleInputChange}
          />
          <InputField
            fullWidth
            label="Final amount (auto-calculated)"
            placeholder="0.00"
            name="finalAmountPreview"
            type="number"
            disabled
            value={computeFinalAmount(
              {
                primaryCharge: data?.cost,
                serviceCharge: data?.serviceCharge,
                extraCharges: data?.extraCharges,
              },
              {
                primaryChargeGst: data?.costGst,
                serviceChargeGst: data?.serviceChargeGst,
                extraChargesGst: data?.extraChargesGst,
              },
              data?.discount
            ).toFixed(2)}
          />
        </div>
      </InfoSection>

      <div className="px-6 py-4 border-t border-app-border flex justify-end gap-4">
        <Button variant="outlined" onClick={() => navigate(-1)}>
          Go Back
        </Button>
        <Button variant="contained" type="submit" disabled={isLoading}>
          {isLoading ? "Saving..." : data?.id ? "Update" : "Submit"}
        </Button>
      </div>
    </div>
  );
};

export default OverviewTab;
